/**
 * EventSessionProvider — durable event-session persistence + recovery.
 *
 * Solves the "Event not found" navigation regressions where:
 *   • browser back / refresh / Android memory reclaim drops React state
 *   • permission dialog / camera capture remount loses context
 *   • tab restore re-renders without route memory
 *
 * Two responsibilities:
 *
 *  1) PERSISTENCE — when a page successfully resolves an event, it calls
 *     `setActiveEvent({ slug, eventId, eventName, role, dashboardHref })`
 *     and we write it to localStorage at key `eventdrop_active_event`.
 *     On app boot we hydrate that store into React context.
 *
 *  2) RECOVERY — `useEventRecovery(slug)` is the hook every guest page
 *     uses instead of raw `axios.get('/events/slug/${slug}')`. It runs
 *     a tiered retry: immediate fetch → 1.2s → 3s → fail. The returned
 *     `status` drives a tiered loading screen (Reconnecting → Restoring
 *     → Failed) so the harsh "Event not found" only shows after every
 *     recovery attempt has been exhausted.
 *
 * Also installs a global popstate listener that flips a session flag
 * `eventdrop_spa_navigated` — BackButton uses this to differentiate
 * "real history" from "fresh tab landing" so back never strands a guest.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import useEventCachePrefetch from '../hooks/useEventCachePrefetch';

const STORAGE_KEY = 'eventdrop_active_event';
const SESSION_FLAG = 'eventdrop_spa_navigated';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Retry schedule (ms) ────────────────────────────────────────────────
// Three attempts: immediate, +1.2s, +3s. Total worst-case ≈ 4.5s before
// the user sees a recoverable failure UX. The middle attempt is what
// catches transient mobile-network blips (the most common production
// cause of "Event not found" reports).
const RETRY_SCHEDULE = [0, 1200, 3000];

const EventSessionContext = createContext(null);

// ── Storage helpers ────────────────────────────────────────────────────
const readStored = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.slug || !parsed?.eventId) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeStored = (data) => {
  if (typeof window === 'undefined') return;
  try {
    if (!data) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* quota / private mode — non-fatal */
  }
};

// ── Provider ──────────────────────────────────────────────────────────
export const EventSessionProvider = ({ children }) => {
  const [activeEvent, setActiveEventState] = useState(() => readStored());

  // Persist whenever activeEvent changes.
  useEffect(() => {
    writeStored(activeEvent);
  }, [activeEvent]);

  // Global popstate listener — flips the SPA-navigated flag so the
  // BackButton can distinguish real history from a fresh tab. We also
  // listen for the first 'click' on any in-app link so the flag flips
  // even before the first popstate.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const markNavigated = () => {
      try { window.sessionStorage.setItem(SESSION_FLAG, '1'); } catch { /* ignore */ }
    };
    // popstate fires on browser back/forward — capture it so we know
    // there's at least one entry behind us.
    window.addEventListener('popstate', markNavigated);
    // 'beforeunload' as a belt-and-braces signal that the user
    // interacted with the SPA at least once.
    const onClick = () => {
      markNavigated();
      window.removeEventListener('click', onClick, true);
    };
    window.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('popstate', markNavigated);
      window.removeEventListener('click', onClick, true);
    };
  }, []);

  const setActiveEvent = useCallback((data) => {
    if (!data) {
      setActiveEventState(null);
      return;
    }
    // Normalise — strip _id / mongo internals, store only what we need.
    const normalized = {
      slug: data.slug,
      eventId: data.eventId || data.event_id,
      eventName: data.eventName || data.event_name || null,
      role: data.role || 'guest',
      dashboardHref: data.dashboardHref || `/event/${data.slug}`,
      joinedAt: data.joinedAt || new Date().toISOString(),
    };
    if (!normalized.slug || !normalized.eventId) return;
    setActiveEventState(normalized);
  }, []);

  const clearActiveEvent = useCallback(() => {
    setActiveEventState(null);
  }, []);

  const value = {
    activeEvent,
    setActiveEvent,
    clearActiveEvent,
  };

  return (
    <EventSessionContext.Provider value={value}>
      {children}
    </EventSessionContext.Provider>
  );
};

// ── Public hook ────────────────────────────────────────────────────────
export const useEventSession = () => {
  const ctx = useContext(EventSessionContext);
  if (!ctx) {
    // Defensive — pages should be inside the provider, but rather than
    // throw and break the page we return a no-op shape.
    return { activeEvent: null, setActiveEvent: () => {}, clearActiveEvent: () => {} };
  }
  return ctx;
};

// ── Recovery hook ──────────────────────────────────────────────────────
/**
 * useEventRecovery(slug) — the canonical replacement for
 * `axios.get('/events/slug/${slug}')` in every guest page.
 *
 * Returns:
 *   {
 *     event:   resolved event payload or null
 *     status:  'reconnecting' | 'restoring' | 'ready' | 'failed'
 *     attempt: 0..3 (debug)
 *     retry:   () => void   — kicks off a fresh recovery cycle
 *   }
 *
 * Status timeline (worst case):
 *   t=0       reconnecting   — first axios.get firing
 *   t=1.2s    restoring      — second attempt; UI escalates copy
 *   t=3s      restoring      — final attempt
 *   t=~4.5s   failed         — recovery exhausted, page may show tier-3
 */
export const useEventRecovery = (slug) => {
  const { activeEvent, setActiveEvent } = useEventSession();
  const [event, setEvent] = useState(null);
  const [status, setStatus] = useState('reconnecting');
  const [attempt, setAttempt] = useState(0);
  const cycleRef = useRef(0);

  const run = useCallback(async () => {
    if (!slug) {
      setStatus('failed');
      return;
    }
    cycleRef.current += 1;
    const myCycle = cycleRef.current;

    // Optimistic hydration — if our localStorage points at the same
    // slug, surface that event immediately so the page never flashes a
    // loader for a returning guest.
    if (activeEvent?.slug === slug && activeEvent.eventId) {
      // We still go on to confirm via axios, but the page can render
      // its "ready" surface while we re-validate in the background.
    }

    for (let i = 0; i < RETRY_SCHEDULE.length; i += 1) {
      if (myCycle !== cycleRef.current) return; // newer cycle started

      if (RETRY_SCHEDULE[i] > 0) {
        // Escalate copy from "reconnecting" → "restoring" once we're
        // past the first attempt — the second tier signals to the
        // guest that we're actively trying alternative paths.
        setStatus('restoring');
        await new Promise((r) => setTimeout(r, RETRY_SCHEDULE[i]));
        if (myCycle !== cycleRef.current) return;
      }

      setAttempt(i + 1);

      try {
        const r = await axios.get(`${API}/events/slug/${slug}`, { timeout: 8000 });
        if (myCycle !== cycleRef.current) return;
        const data = r?.data;
        if (data?.event_id) {
          setEvent(data);
          setStatus('ready');
          // Persist for next visit / refresh.
          setActiveEvent({
            slug: data.slug || slug,
            eventId: data.event_id,
            eventName: data.event_name,
            role: 'guest',
            dashboardHref: `/event/${data.slug || slug}`,
          });
          return;
        }
      } catch (err) {
        // Network / 5xx / timeout — retry. 404 = real "event missing"
        // BUT we still let the retry schedule complete in case it's a
        // transient backend lookup miss (cold-start mongo) within the
        // same cycle. Only after all attempts do we fall through.
        // eslint-disable-next-line no-console
        console.warn('[EventRecovery] attempt', i + 1, 'failed for', slug, err?.response?.status || err?.message);
      }
    }

    if (myCycle !== cycleRef.current) return;
    setStatus('failed');
  }, [slug, activeEvent, setActiveEvent]);

  useEffect(() => {
    setEvent(null);
    setStatus('reconnecting');
    setAttempt(0);
    run();
    // We intentionally only re-run when slug changes — `run` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const retry = useCallback(() => {
    setEvent(null);
    setStatus('reconnecting');
    setAttempt(0);
    run();
  }, [run]);

  // Ambient prefetch — the moment we resolve an event, fan out the
  // tier-1 / tier-2 cache warm-up so subsequent intra-event navigation
  // (Gallery → Spreading → Featured Tonight) is instant.
  useEventCachePrefetch(event?.event_id);

  return { event, status, attempt, retry };
};

export default EventSessionProvider;
